import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getScenes, previewDirect, previewAi, commitScene, checkoutSpecRev, renderScene,
  type ScenesState, type SceneSummary, type EditPreview, type PatchOp, type Caption,
} from '../lib/video-edit';

/**
 * Video scene edit loop (P2). Pick a scene → edit its VO + captions directly or
 * describe a change (AI) → preview the text change → Accept into a versioned,
 * reversible spec → render a scoped draft to watch. Renders are slow + queued, so
 * they're explicit (the text change previews instantly; you render when ready).
 */
export default function VideoSceneEditor({ name }: { name: string }) {
  const [state, setState] = useState<ScenesState | null>(null);
  const [sel, setSel] = useState<number | null>(null);
  const [pending, setPending] = useState<EditPreview | null>(null);
  const [describe, setDescribe] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const nav = useNavigate();

  const load = useCallback(() => { getScenes(name).then(setState).catch(() => setErr('Could not load this video.')); }, [name]);
  useEffect(() => { load(); }, [load]);

  const scenes = pending?.scenes ?? state?.scenes ?? [];
  const scene = sel != null ? scenes[sel] : undefined;

  function selectScene(idx: number) {
    if (pending) return;
    setSel(idx); setDescribe(''); setErr(null); setVideoUrl(null);
  }

  async function applyDirect(patch: PatchOp[]) {
    if (!patch.length) return;
    setBusy('apply'); setErr(null);
    try {
      const r = await previewDirect(name, patch);
      if (!r.ok) { setErr(r.error || 'that edit was not valid'); return; }
      setPending(r);
    } catch { setErr('the edit failed'); } finally { setBusy(null); }
  }
  async function ask() {
    if (sel == null || !describe.trim()) return;
    setBusy('ask'); setErr(null);
    try {
      const r = await previewAi(name, sel, describe.trim());
      if (!r.ok) { setErr(r.error || 'the editor could not make that change'); return; }
      setPending(r); setDescribe('');
    } catch { setErr('the edit failed'); } finally { setBusy(null); }
  }
  async function accept() {
    if (!pending?.patch) return;
    setBusy('commit');
    try {
      const s = await commitScene(name, pending.patch, noteFor(pending, scenes), pending.source ?? 'direct');
      setState(s); setPending(null); setVideoUrl(null);
    } catch { setErr('could not save'); } finally { setBusy(null); }
  }
  async function goToRev(seq: number) {
    if (!state || seq === state.history.headSeq) return;
    setBusy('checkout'); setPending(null);
    try { setState(await checkoutSpecRev(name, seq)); setVideoUrl(null); } catch { /* ignore */ } finally { setBusy(null); }
  }
  async function render() {
    if (!scene) return;
    setBusy('render'); setErr(null); setVideoUrl(null);
    try { setVideoUrl(await renderScene(name, scene.id)); }
    catch (e) { setErr(e instanceof Error ? e.message : 'render failed'); } finally { setBusy(null); }
  }

  if (!state) return <div style={{ padding: 40, color: 'var(--text-dim)' }}>{err || 'Loading…'}</div>;

  return (
    <div style={{ position: 'absolute', inset: 0, display: 'grid', gridTemplateColumns: '250px minmax(0,1fr)', background: 'var(--bg)', color: 'var(--text)' }}>
      {/* rail: versions + scenes */}
      <aside style={{ borderRight: '1px solid var(--border)', background: 'var(--surface)', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <button className="btn" onClick={() => nav('/assets')} style={{ padding: '4px 9px', fontSize: 12 }}>← Assets</button>
          <b style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={state.title}>{state.title}</b>
        </div>
        <div style={{ overflowY: 'auto', minHeight: 0, flex: 1 }}>
          <div className="sec-label" style={{ padding: '12px 14px 6px' }}>Versions</div>
          <div style={{ padding: '0 8px', display: 'flex', flexDirection: 'column', gap: 3 }}>
            {state.history.revisions.map((r) => {
              const head = r.seq === state.history.headSeq;
              return (
                <button key={r.seq} onClick={() => goToRev(r.seq)} disabled={busy === 'checkout'}
                  style={{ textAlign: 'left', padding: '7px 9px', borderRadius: 8, cursor: 'pointer', border: `1px solid ${head ? 'var(--accent)' : 'var(--border)'}`, background: head ? 'var(--accent-soft)' : 'var(--surface-2)', color: head ? 'var(--accent)' : 'var(--text)' }}>
                  <span className="font-mono" style={{ fontSize: 11 }}>v{r.seq}</span>
                  {r.source === 'ai' && <span className="chip" style={{ fontSize: 9, marginLeft: 6, padding: '1px 5px', background: 'var(--ai-soft)', color: 'var(--ai)' }}>AI</span>}
                  <div style={{ fontSize: 12, marginTop: 1 }}>{r.note || (r.kind === 'create' ? 'Authored' : 'Edit')}</div>
                </button>
              );
            })}
          </div>
          <div className="sec-label" style={{ padding: '16px 14px 6px' }}>Scenes</div>
          <div style={{ padding: '0 8px 12px', display: 'flex', flexDirection: 'column', gap: 3 }}>
            {scenes.map((s) => (
              <button key={s.idx} onClick={() => selectScene(s.idx)} disabled={!!pending}
                style={{ textAlign: 'left', padding: '7px 9px', borderRadius: 8, cursor: pending ? 'default' : 'pointer', border: `1px solid ${sel === s.idx ? 'var(--accent)' : 'var(--border)'}`, background: sel === s.idx ? 'var(--accent-soft)' : 'transparent', opacity: pending && sel !== s.idx ? 0.5 : 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span className="font-mono" style={{ fontSize: 10, color: 'var(--text-dim)' }}>{String(s.idx + 1).padStart(2, '0')}</span>
                  <span style={{ fontSize: 12.5, fontWeight: 600 }}>{s.id}</span>
                  <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--text-dim)' }}>{s.sec}s</span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.vo}</div>
              </button>
            ))}
          </div>
        </div>
      </aside>

      {/* main: pending bar / scene editor / player */}
      <div style={{ minWidth: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        {pending && (
          <div style={{ position: 'sticky', top: 0, zIndex: 3, display: 'flex', alignItems: 'center', gap: 10, padding: '9px 16px', background: 'var(--accent)', color: 'var(--accent-ink)' }}>
            <b style={{ fontSize: 13 }}>Preview{pending.source === 'ai' ? ' · AI edit' : ''}</b>
            <span style={{ flex: 1, fontSize: 12, opacity: 0.9 }}>Review the new copy in the scene, then accept.</span>
            <button className="btn" onClick={accept} disabled={busy === 'commit'} style={{ padding: '5px 12px', fontSize: 12.5, background: 'var(--accent-ink)', color: 'var(--accent)', borderColor: 'var(--accent-ink)', fontWeight: 700 }}>{busy === 'commit' ? 'Saving…' : '✓ Accept'}</button>
            <button className="btn" onClick={() => setPending(null)} disabled={busy === 'commit'} style={{ padding: '5px 12px', fontSize: 12.5, background: 'transparent', color: 'var(--accent-ink)', borderColor: 'var(--accent-ink)' }}>Discard</button>
          </div>
        )}

        <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 720, margin: '0 auto', width: '100%' }}>
          {!scene ? (
            <div style={{ color: 'var(--text-dim)', textAlign: 'center', padding: '3rem 1rem' }}>Pick a scene on the left to edit its script and captions, or describe a change.</div>
          ) : (
            <>
              <SceneEditor key={`${sel}-${state.history.headSeq}`} scene={scene} disabled={!!pending || busy === 'apply'} busy={busy === 'apply'} onApply={applyDirect} />
              <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <label className="sec-label" style={{ color: 'var(--ai)' }}>Describe a change</label>
                <textarea className="input" rows={2} value={describe} placeholder="e.g. make the voiceover punchier; tighten the captions…" onChange={(e) => setDescribe(e.target.value)} disabled={!!pending} style={{ resize: 'vertical', fontSize: 13 }} />
                <button className="btn" onClick={ask} disabled={busy === 'ask' || !describe.trim() || !!pending} style={{ background: 'var(--ai-soft)', color: 'var(--ai)', borderColor: 'var(--ai)', fontWeight: 600, justifyContent: 'center' }}>{busy === 'ask' ? 'Asking…' : '✦ Ask AI'}</button>
              </div>

              <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <span className="sec-label">Preview this scene</span>
                  <button className="btn btn-primary" onClick={render} disabled={busy === 'render' || !!pending} style={{ padding: '5px 12px', fontSize: 12.5 }}>{busy === 'render' ? 'Rendering… (~30s)' : '▶ Render draft'}</button>
                  <span style={{ fontSize: 11.5, color: 'var(--text-dim)' }}>Renders the saved scene · queued</span>
                </div>
                {videoUrl && <video controls autoPlay src={videoUrl} style={{ width: '100%', borderRadius: 10, background: '#000', border: '1px solid var(--border)' }} />}
              </div>
              {err && <div style={{ color: 'var(--danger)', background: 'var(--danger-soft)', borderRadius: 8, padding: '8px 12px', fontSize: 12.5 }}>{err}</div>}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function noteFor(p: EditPreview, scenes: SceneSummary[]): string {
  const idx = p.patch?.[0]?.path.match(/\/scenes\/(\d+)/)?.[1];
  const id = idx != null ? scenes[Number(idx)]?.id : undefined;
  return `${p.source === 'ai' ? 'AI edit' : 'Edited'} ${id ? `scene "${id}"` : 'a scene'}`;
}

/** Direct controls for a scene: VO + each caption's text (with the scene's sec slider). */
function SceneEditor({ scene, disabled, busy, onApply }: { scene: SceneSummary; disabled: boolean; busy: boolean; onApply: (p: PatchOp[]) => void }) {
  const init = useMemo(() => ({ vo: scene.vo, captions: scene.captions.map((c) => ({ ...c })), sec: scene.sec }), [scene]);
  const [vo, setVo] = useState(init.vo);
  const [captions, setCaptions] = useState<Caption[]>(init.captions);
  const [sec, setSec] = useState(init.sec);
  useEffect(() => { setVo(init.vo); setCaptions(init.captions); setSec(init.sec); }, [init]);

  function build(): PatchOp[] {
    const ops: PatchOp[] = [];
    if (vo !== init.vo) ops.push({ op: 'replace', path: `/scenes/${scene.idx}/vo`, value: vo });
    if (JSON.stringify(captions) !== JSON.stringify(init.captions)) ops.push({ op: 'replace', path: `/scenes/${scene.idx}/captions`, value: captions });
    if (sec !== init.sec) ops.push({ op: 'replace', path: `/scenes/${scene.idx}/sec`, value: sec });
    return ops;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <h2 className="font-display" style={{ margin: 0, fontSize: 20 }}>{scene.id}</h2>
        <span className="chip" style={{ background: 'var(--surface-2)', color: 'var(--text-dim)' }}>{scene.kind}{scene.variant ? ` · ${scene.variant}` : ''}</span>
      </div>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span className="font-mono" style={{ fontSize: 10.5, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-dim)' }}>Voiceover</span>
        <textarea className="input" rows={2} value={vo} onChange={(e) => setVo(e.target.value)} disabled={disabled} style={{ resize: 'vertical', fontSize: 13.5 }} />
      </label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span className="font-mono" style={{ fontSize: 10.5, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-dim)' }}>Captions</span>
        {captions.map((cap, i) => (
          <input key={i} className="input" value={cap.text} disabled={disabled} onChange={(e) => setCaptions(captions.map((c, j) => (j === i ? { ...c, text: e.target.value } : c)))} style={{ fontSize: 13 }} />
        ))}
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span className="font-mono" style={{ fontSize: 10.5, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-dim)' }}>Length</span>
        <input type="range" min={2} max={20} step={0.5} value={sec} disabled={disabled} onChange={(e) => setSec(Number(e.target.value))} style={{ flex: 1 }} />
        <span className="font-mono" style={{ fontSize: 12 }}>{sec}s</span>
      </label>
      <button className="btn btn-primary" onClick={() => onApply(build())} disabled={disabled || busy} style={{ justifyContent: 'center' }}>{busy ? 'Applying…' : 'Apply changes'}</button>
    </div>
  );
}
